import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import styled from 'styled-components';

import { useComponentDebug } from 'core/hooks/useComponentDebug';

export interface VirtualFileGridItem {
  data: unknown;
}

export interface VirtualFileGridProps {
  items: VirtualFileGridItem[];
  numberOfColumns: number;
  renderItem: (params: { item: VirtualFileGridItem; index: number; columnIndex: number; rowIndex: number; style: React.CSSProperties }) => React.ReactNode;
  className?: string;
  isMobile?: boolean;
}

export interface VirtualFileGridRef {
  scrollToItem: (index: number) => void;
  scrollTo: (options: { top: number }) => void;
  resetAfterIndex: (index: number) => void;
}

const VirtualFileGridContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['rowHeight', 'columns'].includes(prop),
})<{ rowHeight: number; columns: number }>`
  width: 100%;
  height: 100%; /* Use all available height from parent */
  min-height: 200px; /* Ensure minimum height for visibility */
  display: flex;
  flex-direction: column;
  flex: 1; /* Fill available space in the parent flex container */
  overflow: hidden; /* Prevent any overflow */
  background: transparent; /* Prevent white background flash during scroll */

  /* Work WITH Virtuoso instead of fighting it */
  .virtuoso-row {
    display: grid;
    gap: 8px;
    padding: 8px;
    box-sizing: border-box;

    /* Calculate proper column width based on container */
    grid-template-columns: repeat(${(props) => props.columns}, minmax(0, 1fr));

    /* Constrain the total width to container */
    width: 100%;
    max-width: 100%;
    overflow: hidden;

    /* Set consistent row height */
    > * {
      height: ${(props) => props.rowHeight}px;
      min-height: ${(props) => props.rowHeight}px;
      max-height: ${(props) => props.rowHeight}px;
      width: 100%; /* Let grid control the width */
      max-width: 100%;
      overflow: hidden;
    }
  }

  /* Let Virtuoso fill the grid area properly without height dependencies */
  [data-virtuoso-scroller] {
    min-height: 0;
    /* Remove height: 100% dependency - let grid control height */
    display: grid;
    grid-template-rows: 1fr;
  }
`;

const GridItemContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'columns',
})<{ columns: number }>`
  box-sizing: border-box;
  width: 100%;

  /* Let content determine its own height naturally */
  /* Removed forced aspect-ratio to prevent height mismatch */

  /* Ensure content fills container width */
  > * {
    width: 100%;
    border-radius: 4px;
    box-sizing: border-box;
    position: relative;
  }
`;

const VirtualFileGridInner = React.forwardRef<VirtualFileGridRef, VirtualFileGridProps>(
  ({ items, numberOfColumns, renderItem, className, isMobile = false }, ref) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(1200); // Default fallback

    // Debug hook to track virtual file grid rendering
    const { elementRef: debugRef } = useComponentDebug('Core-VirtualFileGrid', [items.length, numberOfColumns, containerWidth, isMobile], {
      itemsCount: items.length,
      numberOfColumns,
      containerWidth,
      isMobile,
      hasRenderItem: !!renderItem,
      className,
    });

    // Track actual container width
    useEffect(() => {
      const updateContainerWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.getBoundingClientRect().width;
          setContainerWidth(width);
        }
      };

      // Initial measurement
      updateContainerWidth();

      // Track resize
      const resizeObserver = new ResizeObserver(updateContainerWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, []);

    // Memoized calculations - only recalculate when key dependencies change
    const actualColumns = useMemo(() => {
      return isMobile ? 1 : numberOfColumns;
    }, [numberOfColumns, isMobile]);

    // Calculate row height based on ACTUAL container width
    const rowHeight = useMemo(() => {
      // Account for padding and gaps: 8px padding on each side + (columns-1) * 8px gaps
      const totalGaps = (actualColumns - 1) * 8;
      const totalPadding = 16; // 8px on each side
      const availableWidth = containerWidth - totalGaps - totalPadding;
      const itemWidth = availableWidth / actualColumns;

      // Thumbnail is square (1:1 aspect ratio)
      const thumbnailHeight = itemWidth;

      // Card structure:
      // - Card padding: 8px top + 8px bottom = 16px
      // - Thumbnail: square (itemWidth)
      // - Info section: ~60px (filename + date + padding)
      // - No extra margins (handled by grid gap)
      const cardPadding = 16;
      const infoSectionHeight = 36;

      const totalHeight = thumbnailHeight + cardPadding + infoSectionHeight;

      return Math.round(totalHeight);
    }, [actualColumns, containerWidth]);

    // Convert flat items into rows for the virtuoso (MEMOIZED to prevent re-render on scroll)
    const rowData = useMemo(() => {
      const rows = [];
      for (let i = 0; i < items.length; i += actualColumns) {
        const rowItems = items.slice(i, i + actualColumns);
        rows.push({ rowItems, startIndex: i });
      }
      return rows;
    }, [items, actualColumns]);

    // Render a single row containing multiple items
    const renderRow = useCallback(
      (index: number) => {
        const rowData_item = rowData[index];
        if (!rowData_item) return null;

        const { rowItems, startIndex } = rowData_item;

        return (
          <div className="virtuoso-row">
            {rowItems.map((item, columnIndex) => {
              const itemIndex = startIndex + columnIndex;
              const rowIndex = Math.floor(itemIndex / actualColumns);

              return (
                <GridItemContainer key={itemIndex} columns={actualColumns}>
                  {renderItem({ item, index: itemIndex, columnIndex, rowIndex, style: {} })}
                </GridItemContainer>
              );
            })}
          </div>
        );
      },
      [rowData, actualColumns, renderItem],
    );

    // Expose grid methods through forwarded ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToItem: (index: number) => {
          const rowIndex = Math.floor(index / actualColumns);
          virtuosoRef.current?.scrollToIndex({ index: rowIndex, behavior: 'smooth' });
        },
        scrollTo: (options: { top: number }) => {
          virtuosoRef.current?.scrollTo({ top: options.top, behavior: 'smooth' });
        },
        resetAfterIndex: (index: number) => {
          // React Virtuoso handles this internally
          console.warn('resetAfterIndex not needed with React Virtuoso');
        },
      }),
      [actualColumns],
    );

    return (
      <div ref={debugRef as React.LegacyRef<HTMLDivElement>} data-component="Core-VirtualFileGrid">
        <VirtualFileGridContainer ref={containerRef} className={className} rowHeight={rowHeight} columns={actualColumns}>
          <Virtuoso
            ref={virtuosoRef}
            totalCount={rowData.length}
            itemContent={renderRow}
            computeItemKey={(index) => `row-${index}`}
            overscan={10}
            style={{
              /* Take full height of grid container */
              width: '100%',
              height: '100%', // Take full height of parent flex container
              flex: 1, // Take all available space in parent flex container
              minHeight: '200px', // Ensure minimum height for visibility
            }}
            scrollSeekConfiguration={{
              enter: (velocity) => Math.abs(velocity) > 1000, // Very high threshold to avoid seek mode
              exit: (velocity) => Math.abs(velocity) < 10, // Low threshold for instant normal mode
              change: () => null, // Disable scroll seek placeholders completely
            }}
            increaseViewportBy={{
              top: isMobile ? 300 : 600, // Mobile: 300px, Desktop: 600px above viewport
              bottom: isMobile ? 300 : 600, // Mobile: 300px, Desktop: 600px below viewport
            }}
          />
        </VirtualFileGridContainer>
      </div>
    );
  },
);

VirtualFileGridInner.displayName = 'VirtualFileGrid';

export const VirtualFileGrid = VirtualFileGridInner;
